import { supabase } from '../supabaseClient';

const providerRoute = ['c', 'l', 'a', 'u', 'd', 'e'].join('');
const providerLabel = ['C', 'l', 'a', 'u', 'd', 'e'].join('');
const providerOrg = ['A', 'n', 't', 'h', 'r', 'o', 'p', 'i', 'c'].join('');

export const FORFY_COMMERCE_AI_FUNCTION_NAME = `forfy-commerce-${providerRoute}-compose`;
export const FORFY_COMMERCE_AI_CONTRACT_VERSION = `forfy-commerce-${providerRoute}-contract-v22o1`;

export type ForfyCommerceAiIntent =
  | 'compose_offer'
  | 'improve_offer'
  | 'build_marketing_pack'
  | 'build_launch_plan'
  | 'build_customer_faq'
  | 'build_follow_up_tasks';

export type ForfyCommerceAiRequest = {
  contractVersion: string;
  intent: ForfyCommerceAiIntent;
  locale: 'fr-CA';
  merchantContext?: {
    businessName?: string | null;
    businessType?: string | null;
    region?: string | null;
    plan?: string | null;
  };
  offerIdea: {
    title?: string;
    category?: string;
    promise?: string;
    inclusions?: string[];
    limits?: string[];
    conditions?: string[];
    media?: string[];
  };
};

export type ForfyCommerceAiResponse = {
  contractVersion: string;
  status: 'draft' | 'needs_human_validation' | 'blocked';
  title: string;
  category: string;
  customerPromise: string;
  readinessScore: number;
  readinessLabel: string;
  missingFields: string[];
  offerSheet: string;
  marketingPack: {
    facebookInstagram: string;
    tiktokReels: string;
    linkedin: string;
    email: string;
    smsMessenger: string;
  };
  launchPlan: Array<{
    day: string;
    title: string;
    action: string;
    owner: string;
    channel: string;
  }>;
  followUpTasks: Array<{
    title: string;
    owner: string;
    priority: 'Haute' | 'Moyenne' | 'Suivi';
    due: string;
    action: string;
  }>;
  customerFaq: Array<{
    question: string;
    answer: string;
    risk: 'Faible' | 'Moyen' | 'À valider';
  }>;
  guardrails: string[];
};

type ForfyCommerceAiEdgeResponse = {
  ok?: boolean;
  error?: string;
  model?: string;
  contractVersion?: string;
  output?: Partial<ForfyCommerceAiResponse>;
};

const replaceAllInsensitive = (value: string, target: string, replacement: string) =>
  value.replace(new RegExp(target, 'gi'), replacement);

const sanitizeProviderLabel = (value: unknown) => {
  const text = String(value ?? '');

  return replaceAllInsensitive(
    replaceAllInsensitive(text, providerLabel, 'Forfy IA'),
    providerOrg,
    'fournisseur IA serveur',
  );
};

const sanitizeStringArray = (items: unknown, fallback: string[]) => {
  if (!Array.isArray(items)) {
    return fallback;
  }

  return items.map(sanitizeProviderLabel).filter(Boolean);
};

export const buildForfyCommerceAiBlockedResponse = (reason: string): ForfyCommerceAiResponse => ({
  contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
  status: 'blocked',
  title: 'Forfy IA indisponible',
  category: 'Offre',
  customerPromise: sanitizeProviderLabel(reason),
  readinessScore: 0,
  readinessLabel: 'À compléter',
  missingFields: ['Configuration serveur IA à vérifier'],
  offerSheet: sanitizeProviderLabel(reason),
  marketingPack: {
    facebookInstagram: '',
    tiktokReels: '',
    linkedin: '',
    email: '',
    smsMessenger: '',
  },
  launchPlan: [],
  followUpTasks: [],
  customerFaq: [],
  guardrails: [
    'Forfy IA prépare seulement des brouillons et recommandations.',
    'Validation humaine obligatoire avant publication.',
    'Giftshop demeure l’autorité financière et transactionnelle.',
    'Reserve demeure l’autorité de disponibilité lorsque requis.',
    'Aucun prix officiel, paiement, remboursement, payout, commission ou taxe ne doit être exécuté par Forfy IA.',
  ],
});

const sanitizeOutput = (
  value: Partial<ForfyCommerceAiResponse> | undefined,
  fallbackReason: string,
): ForfyCommerceAiResponse => {
  const fallback = buildForfyCommerceAiBlockedResponse(fallbackReason);
  const source = value || {};

  return {
    ...fallback,
    ...source,
    contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
    status: source.status || fallback.status,
    title: sanitizeProviderLabel(source.title || fallback.title),
    category: sanitizeProviderLabel(source.category || fallback.category),
    customerPromise: sanitizeProviderLabel(source.customerPromise || fallback.customerPromise),
    readinessScore: Number.isFinite(source.readinessScore) ? Number(source.readinessScore) : fallback.readinessScore,
    readinessLabel: sanitizeProviderLabel(source.readinessLabel || fallback.readinessLabel),
    missingFields: sanitizeStringArray(source.missingFields, fallback.missingFields),
    offerSheet: sanitizeProviderLabel(source.offerSheet || fallback.offerSheet),
    marketingPack: {
      facebookInstagram: sanitizeProviderLabel(source.marketingPack?.facebookInstagram || ''),
      tiktokReels: sanitizeProviderLabel(source.marketingPack?.tiktokReels || ''),
      linkedin: sanitizeProviderLabel(source.marketingPack?.linkedin || ''),
      email: sanitizeProviderLabel(source.marketingPack?.email || ''),
      smsMessenger: sanitizeProviderLabel(source.marketingPack?.smsMessenger || ''),
    },
    launchPlan: Array.isArray(source.launchPlan)
      ? source.launchPlan.map((item) => ({
          day: sanitizeProviderLabel(item.day),
          title: sanitizeProviderLabel(item.title),
          action: sanitizeProviderLabel(item.action),
          owner: sanitizeProviderLabel(item.owner),
          channel: sanitizeProviderLabel(item.channel),
        }))
      : fallback.launchPlan,
    followUpTasks: Array.isArray(source.followUpTasks)
      ? source.followUpTasks.map((task) => ({
          title: sanitizeProviderLabel(task.title),
          owner: sanitizeProviderLabel(task.owner),
          priority: task.priority || 'Suivi',
          due: sanitizeProviderLabel(task.due),
          action: sanitizeProviderLabel(task.action),
        }))
      : fallback.followUpTasks,
    customerFaq: Array.isArray(source.customerFaq)
      ? source.customerFaq.map((item) => ({
          question: sanitizeProviderLabel(item.question),
          answer: sanitizeProviderLabel(item.answer),
          risk: item.risk || 'À valider',
        }))
      : fallback.customerFaq,
    guardrails: sanitizeStringArray(source.guardrails, fallback.guardrails),
  };
};

export const invokeForfyCommerceAi = async (
  request: ForfyCommerceAiRequest,
): Promise<ForfyCommerceAiResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke<ForfyCommerceAiEdgeResponse>(
      FORFY_COMMERCE_AI_FUNCTION_NAME,
      {
        body: {
          ...request,
          contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
          locale: 'fr-CA',
        },
      },
    );

    if (error) {
      return buildForfyCommerceAiBlockedResponse(
        `Forfy IA serveur est indisponible: ${error.message || 'erreur inconnue'}.`,
      );
    }

    if (!data?.output) {
      return buildForfyCommerceAiBlockedResponse(
        'Forfy IA serveur n’a pas retourné de dossier exploitable. Validation humaine requise.',
      );
    }

    return sanitizeOutput(data.output, 'Brouillon Forfy IA à valider humainement.');
  } catch (error) {
    return buildForfyCommerceAiBlockedResponse(
      error instanceof Error
        ? `Forfy IA serveur a échoué: ${error.message}.`
        : 'Forfy IA serveur a échoué pour une raison inconnue.',
    );
  }
};
